'use client';

import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import isEqual from 'lodash/isEqual';
import useResearchTable from '@/creditflowresearch/application/Research/useResearchTable';
import {
  IResearchData,
} from '@/creditflowresearch/domain/vo/research/IResearchData';
import {
  IResearchContextData,
  IResearchContextDataActions,
} from '@/creditflowresearch/domain/vo/research/IResearchContextData';
import { IResearchSSRResponse } from '@/creditflowresearch/domain/vo/research/IResearchResponse';
import { initialResearchState } from '@/creditflowresearch/application/Research/constants';
import {
  filterResearchResponse,
} from '@/creditflowresearch/application/Research/serverSide/helpers';
import { DEFAULT } from '@/creditflowresearch/domain/DocumentGroup';
import { useDocumentGroupContext } from '@/creditflowresearch/application/Research/DocumentGroupContext';
import { useResearchFilterContext } from '@/creditflowresearch/application/Research/ResearchFilterContext';
import { makeEmptyGroupIds } from '@/creditflowresearch/application/Research/utils';

const useResearchData = ({
  response,
  isTile,
  includedDocumentGroup,
  preparedRequestPayload,
}: IResearchSSRResponse) => {
  const filteredResponse = filterResearchResponse(response, includedDocumentGroup.groupId);
  const [researchData, setResearchData] = useState<IResearchData>(
      { ...initialResearchState(isTile), ...filteredResponse });
  const [requestPayload, setRequestPayload] = useState(preparedRequestPayload);
  const { query: { q = '', products, regions } } = useRouter();
  const [searchFieldValue, setSearchFieldValue] = useState(q);
  const [isFirstFetch, setIsFirstFetch] = useState(true);
  const { setEmptyDocumentGroupIds, setDocumentGroup } = useDocumentGroupContext();
  const { filtersPayload } = useResearchFilterContext();
  const isFilterInitialized = useRef(false);
  const prevFiltersPayload = useRef(filtersPayload);

  const actions: IResearchContextDataActions = useResearchTable(
      researchData,
      setResearchData,
      requestPayload,
      searchFieldValue,
      isFirstFetch,
  );

  useEffect(() => {
    if (!isFilterInitialized.current) {
      isFilterInitialized.current = filtersPayload !== null;
      prevFiltersPayload.current = filtersPayload;
      return;
    }

    if (isEqual(prevFiltersPayload.current, filtersPayload)) {
      return;
    }

    prevFiltersPayload.current = filtersPayload;
    actions.paginate(1);
  }, [filtersPayload]);

  useEffect(() => {
    actions.paginate(1);
  }, [products, regions]);

  useEffect(() => setRequestPayload(preparedRequestPayload), [preparedRequestPayload]);

  useEffect(() => {
    const data = { ...initialResearchState(isTile), ...filteredResponse } as IResearchData;
    const emptyGroupIds = makeEmptyGroupIds(response);

    setEmptyDocumentGroupIds(emptyGroupIds);
    setDocumentGroup(emptyGroupIds.includes(includedDocumentGroup.groupId) ? DEFAULT : includedDocumentGroup);

    setResearchData(data);

    setIsFirstFetch(false);
  }, [response, includedDocumentGroup]);

  return {
    researchData,
    searchFieldValue,
    setSearchFieldValue,
    isFirstFetch,
    ...actions,
  };
};

export const ResearchContext = createContext<IResearchContextData>(null);

export const useResearchContext = () => {
  const context: IResearchContextData = useContext(ResearchContext);
  if (!context) {
    throw new Error('useResearchContext must be used within the ResearchContext');
  }
  return context;
};

const ResearchContextProvider = ({ children, data }) => (
  <ResearchContext.Provider value={ useResearchData(data) }>
    { children }
  </ResearchContext.Provider>
);

export default ResearchContextProvider;
